import { useState } from 'react'
import { Link } from 'react-router-dom'

// Tokenomics data
const tokenomics = [
  { label: 'Dev/Team', percent: 20, amount: '200M', color: 'from-cyan-500 to-cyan-600', description: '6 month lock, 12 month vesting' },
  { label: 'Community Airdrop', percent: 30, amount: '300M', color: 'from-purple-500 to-purple-600', description: 'For early users & testers' },
  { label: 'Liquidity Pool', percent: 25, amount: '250M', color: 'from-blue-500 to-blue-600', description: 'Locked in DEX' },
  { label: 'Marketing', percent: 15, amount: '150M', color: 'from-pink-500 to-pink-600', description: 'Growth & partnerships' },
  { label: 'Treasury', percent: 10, amount: '100M', color: 'from-amber-500 to-amber-600', description: 'DAO controlled reserve' },
]

const utilities = [
  { icon: '🎯', title: 'Premium Features', desc: 'Access advanced K.I.T. trading tools and AI strategies' },
  { icon: '📊', title: 'Trading Signals', desc: 'Pay for premium trading signals with $KIT' },
  { icon: '🗳️', title: 'Governance', desc: 'Vote on K.I.T. development decisions' },
  { icon: '💰', title: 'Staking Rewards', desc: 'Stake $KIT to earn passive income' },
  { icon: '💸', title: 'Fee Discounts', desc: 'Reduced trading fees when paying with $KIT' },
  { icon: '🎁', title: 'Airdrops', desc: 'Exclusive airdrops for $KIT holders' },
]

const roadmap = [
  { quarter: 'Q1 2026', title: 'Token Launch', items: ['SPL Token Creation', 'Initial DEX Offering', 'Community Airdrop', 'CoinGecko Listing'] },
  { quarter: 'Q2 2026', title: 'Staking & Utility', items: ['Staking Platform Launch', 'Premium Feature Integration', 'Partnership Announcements', 'Exchange Listings'] },
  { quarter: 'Q3 2026', title: 'Governance', items: ['DAO Launch', 'Governance Voting', 'Treasury Management', 'Community Proposals'] },
  { quarter: 'Q4 2026', title: 'Expansion', items: ['Cross-chain Bridge', 'Mobile App Integration', 'Institutional Features', 'Global Marketing'] },
]

const faqs = [
  { q: 'What is $KIT?', a: '$KIT is the native utility token of the K.I.T. (Künstliche Intelligenz Trading) ecosystem. It powers AI-driven trading features, governance, and rewards.' },
  { q: 'How can I buy $KIT?', a: 'You can buy $KIT on Solana DEXs like Jupiter or Raydium. Connect your Phantom or Solflare wallet and swap SOL for $KIT.' },
  { q: 'Is the token audited?', a: 'The token contract follows Solana SPL standards. Mint and freeze authorities will be revoked after launch for maximum security.' },
  { q: 'What blockchain is $KIT on?', a: '$KIT is built on Solana for fast transactions (<1 second) and minimal fees (<$0.01).' },
  { q: 'How can I earn $KIT?', a: 'Earn $KIT through staking, participating in community events, providing liquidity, or as rewards for using K.I.T. trading features.' },
]

export default function Token() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-pink-500/10" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6 py-24 text-center">
          {/* Token Logo */}
          <div className="mb-8 flex justify-center">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-600 flex items-center justify-center text-5xl font-bold shadow-2xl shadow-cyan-500/30 border-4 border-white/10">
              $KIT
            </div>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">
              K.I.T. Token
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Power the Future of AI Trading
          </p>
          
          <p className="text-gray-400 mb-10 max-w-2xl mx-auto">
            $KIT is the utility token driving the K.I.T. ecosystem — enabling premium features, 
            governance participation, staking rewards, and access to the most advanced AI trading technology.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://jup.ag/swap/SOL-9Uf9itx36uPv66jbTUpCjDDsYYeiRousAVkPfRQBcJH4"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl font-semibold text-lg hover:from-cyan-400 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50"
            >
              Buy $KIT
            </a>
            <a
              href="#tokenomics"
              className="px-8 py-4 bg-white/10 border border-white/20 rounded-xl font-semibold text-lg hover:bg-white/20 transition-all"
            >
              View Tokenomics
            </a>
          </div>
          
          {/* Token Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: 'Total Supply', value: '1B $KIT' },
              { label: 'Blockchain', value: 'Solana' },
              { label: 'Transaction Fee', value: '<$0.01' },
              { label: 'Transaction Speed', value: '<1 sec' },
            ].map((stat) => (
              <div key={stat.label} className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                <div className="text-2xl md:text-3xl font-bold text-cyan-400">{stat.value}</div>
                <div className="text-gray-400 text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tokenomics Section */}
      <section id="tokenomics" className="py-24 bg-gray-900/50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4">
            <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
              Tokenomics
            </span>
          </h2>
          <p className="text-gray-400 text-center mb-16 max-w-2xl mx-auto">
            Fair distribution designed for long-term sustainability and community growth
          </p>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Pie Chart Visual */}
            <div className="relative">
              <div className="aspect-square max-w-md mx-auto">
                <svg viewBox="0 0 100 100" className="transform -rotate-90">
                  {tokenomics.reduce((acc, item, i) => {
                    const startAngle = acc.angle
                    const angle = (item.percent / 100) * 360
                    const endAngle = startAngle + angle
                    const largeArc = angle > 180 ? 1 : 0
                    const startX = 50 + 40 * Math.cos((startAngle * Math.PI) / 180)
                    const startY = 50 + 40 * Math.sin((startAngle * Math.PI) / 180)
                    const endX = 50 + 40 * Math.cos((endAngle * Math.PI) / 180)
                    const endY = 50 + 40 * Math.sin((endAngle * Math.PI) / 180)
                    
                    acc.elements.push(
                      <path
                        key={i}
                        d={`M 50 50 L ${startX} ${startY} A 40 40 0 ${largeArc} 1 ${endX} ${endY} Z`}
                        className={`fill-current ${
                          i === 0 ? 'text-cyan-500' :
                          i === 1 ? 'text-purple-500' :
                          i === 2 ? 'text-blue-500' :
                          i === 3 ? 'text-pink-500' : 'text-amber-500'
                        }`}
                        style={{ opacity: 0.9 }}
                      />
                    )
                    acc.angle = endAngle
                    return acc
                  }, { angle: 0, elements: [] as JSX.Element[] }).elements}
                  <circle cx="50" cy="50" r="25" className="fill-gray-950" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-3xl font-bold">1B</div>
                    <div className="text-gray-400 text-sm">Total Supply</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Legend */}
            <div className="space-y-4">
              {tokenomics.map((item) => (
                <div key={item.label} className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-cyan-500/30 transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full bg-gradient-to-r ${item.color}`} />
                      <span className="font-semibold">{item.label}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-400">{item.amount}</span>
                      <span className="text-cyan-400 font-bold">{item.percent}%</span>
                    </div>
                  </div>
                  <div className="text-gray-500 text-sm ml-7">{item.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Utility Section */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4">
            <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
              Token Utility
            </span>
          </h2>
          <p className="text-gray-400 text-center mb-16 max-w-2xl mx-auto">
            $KIT powers every aspect of the K.I.T. Trading ecosystem
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {utilities.map((item) => (
              <div key={item.title} className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-cyan-500/30 transition-all group">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">{item.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section className="py-24 bg-gray-900/50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4">
            <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
              Roadmap
            </span>
          </h2>
          <p className="text-gray-400 text-center mb-16 max-w-2xl mx-auto">
            Our journey to revolutionize AI trading
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {roadmap.map((phase, i) => (
              <div key={phase.quarter} className="relative">
                {i < roadmap.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-cyan-500/50 to-transparent z-0" />
                )}
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-cyan-500/30 transition-all relative z-10">
                  <div className="text-cyan-400 font-bold mb-2">{phase.quarter}</div>
                  <h3 className="text-xl font-semibold mb-4">{phase.title}</h3>
                  <ul className="space-y-2">
                    {phase.items.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-gray-400 text-sm">
                        <span className="text-cyan-500">✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Buy Section */}
      <section id="how-to-buy" className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4">
            <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
              How to Buy $KIT
            </span>
          </h2>
          <p className="text-gray-400 text-center mb-16">
            Get started in just a few simple steps
          </p>
          
          <div className="space-y-6">
            {[
              { step: 1, title: 'Get a Solana Wallet', desc: 'Download Phantom or Solflare wallet extension for your browser', icon: '👛' },
              { step: 2, title: 'Buy SOL', desc: 'Purchase SOL on an exchange like Coinbase or Binance and send to your wallet', icon: '💰' },
              { step: 3, title: 'Connect to DEX', desc: 'Go to Jupiter (jup.ag) or Raydium and connect your wallet', icon: '🔗' },
              { step: 4, title: 'Swap for $KIT', desc: 'Enter the $KIT token address and swap your SOL for $KIT', icon: '🔄' },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-6 bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center text-2xl font-bold flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{item.icon}</span>
                    <h3 className="text-xl font-semibold">{item.title}</h3>
                  </div>
                  <p className="text-gray-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 p-6 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-2xl border border-cyan-500/30 text-center">
            <p className="text-gray-300 mb-4">Token Contract Address (Solana)</p>
            <code className="text-cyan-400 bg-black/30 px-4 py-2 rounded-lg font-mono text-sm select-all cursor-pointer" onClick={() => navigator.clipboard.writeText('9Uf9itx36uPv66jbTUpCjDDsYYeiRousAVkPfRQBcJH4')}>
              9Uf9itx36uPv66jbTUpCjDDsYYeiRousAVkPfRQBcJH4
            </code>
            <p className="text-gray-500 text-sm mt-2">Click to copy • <a href="https://solscan.io/token/9Uf9itx36uPv66jbTUpCjDDsYYeiRousAVkPfRQBcJH4" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">View on Solscan</a></p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-900/50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4">
            <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
              FAQ
            </span>
          </h2>
          <p className="text-gray-400 text-center mb-16">
            Frequently asked questions about $KIT
          </p>
          
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-semibold">{faq.q}</span>
                  <span className={`text-cyan-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4 text-gray-400">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Join the <span className="text-cyan-400">AI Trading Revolution</span>?
          </h2>
          <p className="text-gray-400 mb-10 max-w-2xl mx-auto">
            Be part of the future. Get $KIT tokens and unlock the full potential of K.I.T. Trading.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/"
              className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl font-semibold text-lg hover:from-cyan-400 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/30"
            >
              Explore K.I.T. Trading
            </Link>
            <a
              href="https://discord.gg/xqcbj6sp"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white/10 border border-white/20 rounded-xl font-semibold text-lg hover:bg-white/20 transition-all"
            >
              Join Discord
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 text-center text-gray-500 text-sm">
          <p>© 2026 K.I.T. Trading. All rights reserved.</p>
          <p className="mt-2">$KIT is a utility token. Please do your own research before investing.</p>
        </div>
      </footer>
    </div>
  )
}
